/**
 * Nosto Tracking Utilities
 * Handles api.recordSearch() calls with proper data structures
 */

// Track which products have already been viewed to prevent duplicates
// Use sessionStorage to persist across component re-mounts
const getViewedProducts = () => {
    if (typeof window === 'undefined') return new Set();
    try {
        const stored = sessionStorage.getItem('nosto_viewed_products');
        return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
        return new Set();
    }
};

const setViewedProducts = (viewedProducts) => {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.setItem('nosto_viewed_products', JSON.stringify([...viewedProducts]));
    } catch {
        // Fallback to in-memory only
    }
};

/**
 * Clear the tracking cache (useful for testing or page refreshes)
 */
export const clearTrackingCache = () => {
    if (typeof window !== 'undefined') {
        try {
            sessionStorage.removeItem('nosto_viewed_products');
        } catch {
            // Ignore errors
        }
    }
}

/**
 * Record a View Product event with deduplication
 * @param {string} productId - Product ID
 * @param {string} ref - Reference/placement
 * @param {string} type - Event type (default: 'vp')
 */
export const recordViewProduct = (productId, ref, type = 'vp') => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    // Create a unique key for this product + ref combination
    const eventKey = `${productId}-${ref}-${type}`

    // Get current viewed products from persistent storage
    const viewedProducts = getViewedProducts();

    // Check if we've already tracked this event
    if (viewedProducts.has(eventKey)) {
        return
    }

    // Mark this event as tracked
    viewedProducts.add(eventKey)
    setViewedProducts(viewedProducts);

    try {
        window.nostojs((api) => {
            api.recordAttribution({
                type: type,
                target: productId,
                ref: ref,
            })
                .done()
                .then((data) => {
                    // Success - no logging needed
                })
                .catch((error) => {
                    console.error(`❌ Nosto View Product event error:`, error)
                    // Remove from set if tracking failed so we can retry
                    const viewedProducts = getViewedProducts();
                    viewedProducts.delete(eventKey)
                    setViewedProducts(viewedProducts);
                })
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordAttribution for View Product:', error)
        // Remove from set if tracking failed so we can retry
        const viewedProducts = getViewedProducts();
        viewedProducts.delete(eventKey)
        setViewedProducts(viewedProducts);
    }
}

/**
 * Record search submit (serp) analytics
 * @param {string} query - Search query
 * @param {Array} products - Array of product objects
 */
export const recordSearchSubmit = (query, products) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    const processedProducts = products.map(product => ({
        productId: product.productId,
        name: product.name,
        url: product.url,
        imageUrl: product.imageUrl,
        price: product.price,
        listPrice: product.listPrice,
        brand: product.brand,
        availability: product.availability
    }))

    try {
        window.nostojs((api) => {
            // Use the same format as the working search page example
            const searchData = {
                query: query,
                products: processedProducts
            }

            api.recordSearch(query, searchData.products)
                .done()
                .then((response) => {
                    console.log(`✅ Nosto search submit analytics recorded successfully:`, response)
                })
                .catch((error) => {
                    console.error(`❌ Nosto search submit analytics error:`, error)
                })
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearch for submit:', error)
    }
}

/**
 * Record search input/autocomplete analytics
 * @param {string} query - Search query
 * @param {Array} products - Array of product objects
 */
export const recordSearchAutocomplete = (query, products) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    // Add debugging to understand the data structure
    console.log('🔍 recordSearchAutocomplete called with:', { query, productsCount: products?.length, sampleProduct: products?.[0] })

    // Validate that products have the required structure
    if (!Array.isArray(products) || products.length === 0) {
        console.warn('❌ recordSearchAutocomplete: No valid products provided')
        return
    }

    try {
        window.nostojs((api) => {
            // Create the search request object as expected by the API
            const searchRequest = {
                query: query,
                products: {
                    fields: ['name', 'productId', 'url', 'imageUrl', 'price', 'listPrice', 'brand'],
                    size: products.length
                }
            }

            // Create the search result object as expected by the API
            const searchResult = {
                products: {
                    hits: products.map(product => ({
                        productId: product.productId || product.id || '',
                        name: product.name || product.productName || '',
                        url: product.url || '',
                        imageUrl: product.imageUrl || product.image || '',
                        price: product.price || 0,
                        listPrice: product.listPrice || product.compareAtPrice || 0,
                        brand: product.brand || 'Monica + Andy'
                    })),
                    total: products.length,
                    size: products.length,
                    from: 0
                }
            }

            console.log('🔍 Calling api.recordSearch with correct format:', {
                type: 'autocomplete',
                searchRequest,
                searchResult
            })

            // Use the correct API format according to Nosto docs
            const result = api.recordSearch(
                'autocomplete',  // type
                searchRequest,    // request
                searchResult,     // result
                { isKeyword: false }  // options
            )

            // Check if the method returned a promise-like object
            if (result && typeof result.done === 'function') {
                result
                    .done()
                    .then((response) => {
                        console.log('✅ Nosto autocomplete analytics recorded successfully')
                    })
                    .catch((error) => {
                        console.error(`❌ Nosto autocomplete analytics error:`, error)
                    })
            } else {
                console.warn('⚠️ api.recordSearch did not return a promise-like object:', result)
                // Try alternative approach - just call the method without chaining
                console.log('✅ Nosto autocomplete analytics called (no promise returned)')
            }
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearch for autocomplete:', error)
    }
}

/**
 * Record category/collection search analytics
 * @param {string} categoryId - Category/collection ID
 * @param {Array} products - Array of product objects
 * @param {Object} filters - Applied filters (optional)
 */
export const recordCategorySearch = (categoryId, products, filters = {}) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    // Skip category tracking - api.recordSearch only works for search queries
    return

    const processedProducts = products.map(product => ({
        productId: product.productId,
        name: product.name,
        url: product.url,
        imageUrl: product.imageUrl,
        price: product.price,
        listPrice: product.listPrice,
        brand: product.brand,
        availability: product.availability
    }))

    try {
        window.nostojs((api) => {
            console.log(`🔍 Recording Nosto category analytics:`, { categoryId, products: processedProducts, filters })

            // Use simple format that works with the current API
            api.recordSearch(`category:${categoryId}`, processedProducts)
                .done()
                .then((response) => {
                    console.log(`✅ Nosto category analytics recorded successfully:`, response)
                })
                .catch((error) => {
                    console.error(`❌ Nosto category analytics error:`, error)
                })
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearch for category:', error)
    }
}

/**
 * Record category search with path (for collection pages)
 * @param {string} categoryPath - Category path
 * @param {Array} products - Array of product objects
 * @param {Object} filters - Applied filters (optional)
 */
export const recordCategorySearchByPath = (categoryPath, products, filters = {}) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    // Skip category tracking - api.recordSearch only works for search queries
    return

    const processedProducts = products.map(product => ({
        productId: product.productId,
        name: product.name,
        url: product.url,
        imageUrl: product.imageUrl,
        price: product.price,
        listPrice: product.listPrice,
        brand: product.brand,
        availability: product.availability
    }))

    try {
        window.nostojs((api) => {
            console.log(`🔍 Recording Nosto category path analytics:`, { categoryPath, products: processedProducts, filters })

            // Use simple format that works with the current API
            api.recordSearch(`category:${categoryPath}`, processedProducts)
                .done()
                .then((response) => {
                    console.log(`✅ Nosto category path analytics recorded successfully:`, response)
                })
                .catch((error) => {
                    console.error(`❌ Nosto category path analytics error:`, error)
                })
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearch for category path:', error)
    }
}

/**
 * Record search product click analytics
 * @param {string} type - Search type: 'serp', 'autocomplete', 'category'
 * @param {Object} hit - Product object containing productId and url
 */
export const recordSearchClick = (type, hit) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    try {
        window.nostojs((api) => {
            api.recordSearchClick(type, {
                productId: hit.productId,
                url: hit.url
            })
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearchClick:', error)
    }
}

/**
 * Record search add to cart analytics
 * @param {string} type - Search type: 'serp', 'autocomplete', 'category'
 * @param {Object} hit - Product object containing productId and url
 */
export const recordSearchAddToCart = (type, hit) => {
    if (typeof window === 'undefined' || !window.nostojs) {
        return
    }

    try {
        window.nostojs((api) => {
            api.recordSearchAddToCart(type, {
                productId: hit.productId,
                url: hit.url
            })
        })
    } catch (error) {
        console.error('❌ Error calling Nosto recordSearchAddToCart:', error)
    }
}
